import { useMemo } from "react"
import { filter as _filter, map, hasIn, find, chain, cloneDeep, omit, get, upperCase, size, isEmpty, keys, compact, union, set, isObject } from "lodash"
import update from "immutability-helper"
import { useFilterSecurities } from "resources/filters"
import { useClassifications, useClassificationsValues } from "resources/classifications"
import { computeRestoreFilterOn, STRING_OPERATORS, NUMERIC_OPERATORS, NUMERIC_AND_BOOL_OPERATORS, CLASSIFICATION_OPERATORS, SECURITIES_OPERATORS } from "./filters/filter-ons"
import { compareId } from "./securities"
import { ASSET_TYPES } from "./assets/constants"
import { combineTanStackStatuses } from "./data-statuses"
import { FILTER_TYPES } from "./filters/chips"

const EMPTY_ARRAY = []

export const factorSensitivityTypeMap = {
  predictedFactorSensitivity: "PREDICTED",
  historicalFactorSensitivity: "HISTORICAL",
}

export const isFilterValid = (filter) => {
  if (!filter.filterOn) return false
  if (!filter.filterOn.operators) return true
  if (!filter.operator) return false
  if (!filter.operator?.validator) return true
  return filter.operator.validator(filter.values)
}

const computeAddMembershipFilter = (filterType, operator, id, dataType, values, filter, editingFilter) => {
  const subQuery = {
    ...operator.queryFormatter(id, operator.value, dataType, ...values),
    id,
  }

  if (!get(filter, filterType)) {
    return update(filter || {}, {
      [filterType]: { $set: [subQuery] },
    })
  }

  if (editingFilter) {
    return update(filter || {}, {
      [filterType]: { [editingFilter.id]: { $set: subQuery } },
    })
  }

  return update(filter || {}, {
    [filterType]: { $push: [subQuery] },
  })
}

export function mapFactorExposureNegativeBoolOperator(filter) {
  return {
    ...filter,
    factorExposure: map(filter.factorExposure, expFilter => {
      const { id, contentSetId } = expFilter
      if (hasIn(expFilter, "hasNoValue")) {
        return { id, contentSetId, hasValue: false }
      }
      return expFilter
    }),
  }
}

export const computeEditingFilter = (editingFilter, editing, filterOns, securitiesDescriptors) => {
  if (editing) {
    let filter
    let filterOn
    let values
    let operator

    const filterType = _filter(filterOns, filterOn => filterOn.type === editing.filterType)

    if (editing.filterType === "descriptor") {
      filter = editingFilter[editing.id]
      filterOn = computeRestoreFilterOn(filterType, editing.id)
    } else if (editing.filterType === "longshort") {
      filter = editingFilter[editing.id]
      filterOn = computeRestoreFilterOn(filterType, filter)
    } else if (editing.filterType === "securities") {
      filter = editingFilter[editing.filterType]
      filterOn = computeRestoreFilterOn(filterType, "securities")
    } else {
      filter = editingFilter[editing.filterType][editing.id]
      filterOn = computeRestoreFilterOn(filterType, filter.type || filter.id)
    }

    if (filterOn.operators) {
      operator = find(filterOn.operators, operator => filter[operator.value] !== undefined)

      switch (operator.selectorType) {
        case "securitiesSearch":
          values = map(
            filter[operator.value],
            (security) => ({
              ...security,
              country: chain(securitiesDescriptors)
                .find(security => compareId(security, security))
                .get("country")
                .value(),
            }),
          )
          break
        case "multiSelect":
          values = map(filter[operator.value], value => find(filterOn.options, o => o.value === value || o.value === value.value))
          break
        case "singleSelect":
          values = [find(filterOn.options, v => v.value === filter[operator.value] || v.value === filter[operator.value].value)]
          break
        case "range":
          values = map(filter[operator.value], value => value)
          break
        default:
          values = [filter[operator.value]]
          break
      }
    }

    if (filterOn.dataType === "millions") {
      values = map(values, value => value / 1000000)
    }

    return {
      descriptor: editing.id,
      filter: {
        filterOn,
        operator,
        values,
      },
    }
  }

  return null
}

export const computeRawFilter = (editingFilter, editing, filterOns, securitiesDescriptors) => {
  if (editing) {
    let filter
    let filterOn
    let values
    let operator
    let isUnsupported = false

    const filterOnByType = filterOns.filter(filterOn => filterOn.type === editing.filterType)

    if (editing.filterType === "descriptor") {
      filter = editingFilter[editing.id]
      filterOn = computeRestoreFilterOn(filterOnByType, editing.id)
    } else if (editing.filterType === "longshort") {
      filter = editingFilter[editing.id]
      filterOn = computeRestoreFilterOn(filterOnByType, filter)
    } else if (editing.filterType === "securities") {
      filter = editingFilter[editing.filterType]
      filterOn = computeRestoreFilterOn(filterOnByType, "securities")
    } else if (["predictedFactorSensitivity", "historicalFactorSensitivity"].includes(editing.filterType)) {
      filter = get(editingFilter, ["factorSensitivity", editing.id], {})
      filterOn = computeRestoreFilterOn(filterOnByType, filter.id)
    } else {
      filter = get(editingFilter, [editing.filterType, editing.id], {})
      filterOn = computeRestoreFilterOn(filterOnByType, filter.type || filter.id)
    }

    if (!filterOn) {
      isUnsupported = true
      filterOn = createFallbackFilterOn(editing.filterType, editing.filterType === "longshort" ? filter : editing.id)
    }

    if (filterOn && filterOn.operators) {
      operator = find(filterOn.operators, operator => filter[operator.value] !== undefined)

      if (operator && !isUnsupported) {
        switch (operator.selectorType) {
          case "securitiesSearch":
            values = map(
              filter[operator.value],
              (security) => ({
                ...security,
                country: chain(securitiesDescriptors)
                  .find(security => compareId(security, security))
                  .get("country")
                  .value(),
              }),
            )
            break
          case "multiSelect":
            values = map(filter[operator.value], value => find(filterOn.options, o => o.value === value || o.value === value.value))
            break
          case "singleSelect":
            values = [find(filterOn.options, v => v.value === filter[operator.value] || v.value === filter[operator.value].value)]
            break
          case "range":
            values = map(filter[operator.value], value => value)
            break
          default:
            values = [filter[operator.value]]
            break
        }
      } else if (editing.filterType === "longshort") {
        values = [filter]
      }
    }

    if (filterOn?.dataType === "millions") {
      values = map(values, value => value / 1000000)
    }

    if (isUnsupported) {
      if (editing.filterType === "longshort") {
        values = [filter]
      } else if (operator) {
        const rawValue = filter[operator.value]
        const rawValues = Array.isArray(rawValue) ? rawValue : [rawValue]

        switch (operator.selectorType) {
          case "multiSelect":
          case "singleSelect":
            values = rawValues.map(val => {
              if (isObject(val) && val.value !== undefined) {
                return val
              }
              return { value: val, label: val }
            })
            break
          case "securitiesSearch":
            values = rawValues.map(val => {
              if (isObject(val) && (val.ticker || val.id)) {
                return val
              }
              return { value: val, label: val }
            })
            break
          case "range":
          default:
            values = rawValues
            break
        }
      }
    }

    return {
      descriptor: editing.id,
      filter: {
        filterOn,
        operator,
        values: values || [],
      },
      isUnsupported,
    }
  }

  return null
}

function createFallbackFilterOn(filterType, id) {
  const filterTypeDef = FILTER_TYPES.find(ft => ft.field === id)
  const getLabel = () => {
    if (filterTypeDef?.label) {
      if (typeof filterTypeDef.label === "object") {
        return filterTypeDef.label[id] || id
      }
      return filterTypeDef.label
    }
    return id
  }

  switch (filterType) {
    case "descriptor":
      return {
        label: getLabel(),
        type: "descriptor",
        dataType: "string",
        value: id,
        operators: STRING_OPERATORS,
        options: [],
      }
    case "securities":
      return {
        id: "securities",
        label: "Securities",
        type: "securities",
        dataType: "string",
        value: "securities",
        operators: SECURITIES_OPERATORS,
        options: [],
      }
    case "classification":
      return {
        label: "Classification",
        type: "classification",
        dataType: "string",
        value: id,
        operators: CLASSIFICATION_OPERATORS,
        options: [],
      }
    case "risk":
      return {
        label: "Risk",
        type: "risk",
        dataType: "percent",
        value: id,
        operators: NUMERIC_OPERATORS,
        options: [],
      }
    case "factorExposure":
      return {
        label: "Factor Exposure",
        type: "factorExposure",
        dataType: "numeric",
        value: id,
        operators: NUMERIC_AND_BOOL_OPERATORS,
        options: [],
      }
    case "factorSensitivity":
      return {
        label: "Factor Sensitivity",
        type: "factorSensitivity",
        dataType: "numeric",
        value: id,
        operators: NUMERIC_AND_BOOL_OPERATORS,
        options: [],
      }
    case "beta":
      return {
        label: "Beta",
        type: "beta",
        dataType: "numeric",
        value: id,
        operators: NUMERIC_OPERATORS,
        options: [],
      }
    case "tag":
      return {
        label: "Tags",
        type: "tag",
        dataType: "string",
        value: id,
        operators: STRING_OPERATORS,
        options: [],
      }
    case "positionAttributes":
      return {
        id: id,
        label: "Position Attributes",
        type: "positionAttributes",
        dataType: "string",
        value: id,
        operators: STRING_OPERATORS,
        options: [],
      }
    case "longshort":
      return {
        label: id === "LONG" ? "Long" : id === "SHORT" ? "Short" : "Long/Short",
        type: "longshort",
        dataType: "constant",
        value: id,
        operators: [],
        options: [],
      }
    default:
      return {
        label: getLabel(),
        type: filterType,
        dataType: "string",
        value: id,
        operators: STRING_OPERATORS,
        options: [],
      }
  }
}

export function computeAddFilter(originalFilter, type, dataType, id, operator, values, editingFilter, contentSetId) {
  let filter = cloneDeep(originalFilter)

  if (editingFilter && editingFilter.id !== id) {
    filter = omit(filter, editingFilter.id)
  }

  switch (type) {
    case "longshort":
      filter = {
        ...filter,
        longShort: id,
      }
      break
    case "descriptor":
      filter = update(filter || {}, {
        [id]: { $set: operator.queryFormatter(id, operator.value, dataType, ...values) },
      })
      break
    case "risk": {
      const subQuery = operator.queryFormatter(id, operator.value, dataType, ...values)
      subQuery.type = id
      if (!get(filter, "risk")) {
        filter = update(filter || {}, {
          risk: { $set: [subQuery] },
        })
      } else if (editingFilter) {
        filter = update(filter || {}, {
          risk: { [editingFilter.id]: { $set: subQuery } },
        })
      } else {
        filter = update(filter || {}, {
          risk: { $push: [subQuery] },
        })
      }
      break
    }
    case "factorExposure": {
      const subQuery = operator.queryFormatter(id, operator.value, dataType, ...values)
      subQuery.id = id
      if (contentSetId) {
        subQuery.contentSetId = contentSetId
      }

      if (!get(filter, "factorExposure")) {
        filter = update(filter || {}, {
          factorExposure: { $set: [subQuery] },
        })
      } else if (editingFilter) {
        filter = update(filter || {}, {
          factorExposure: { [editingFilter.id]: { $set: subQuery } },
        })
      } else {
        filter = update(filter || {}, {
          factorExposure: { $push: [subQuery] },
        })
      }
      break
    }
    case "predictedFactorSensitivity":
    case "historicalFactorSensitivity": {
      const subQuery = operator.queryFormatter(id, operator.value, dataType, ...values)
      subQuery.id = id
      subQuery.type = get(factorSensitivityTypeMap, type)

      if (!get(filter, "factorSensitivity")) {
        filter = update(filter || {}, {
          factorSensitivity: { $set: [subQuery] },
        })
      } else if (editingFilter) {
        filter = update(filter || {}, {
          factorSensitivity: { [editingFilter.id]: { $set: subQuery } },
        })
      } else {
        filter = update(filter || {}, {
          factorSensitivity: { $push: [subQuery] },
        })
      }
      break
    }
    case "universe": {
      const subQuery = {
        type: upperCase(id),
        [operator.value]: values[0],
      }

      if (!get(filter, "universe")) {
        filter = update(filter || {}, {
          universe: { $set: [subQuery] },
        })
      } else if (editingFilter) {
        filter = update(filter || {}, {
          universe: { [editingFilter.id]: { $set: subQuery } },
        })
      } else {
        filter = update(filter || {}, {
          universe: { $push: [subQuery] },
        })
      }
      break
    }
    case "securities": {
      const formattedSecurities = map(values, security => chain(security)
        .pick(["ticker", "mic", "country", "indexId", "internalId", "assetClass"])
        .omitBy(value => !value)
        .value(),
      )

      const subQuery = {
        [operator.value]: formattedSecurities,
      }

      if (!get(filter, "securities") || editingFilter) {
        filter = update(filter || {}, {
          securities: { $set: subQuery },
        })
      } else {
        filter = update(filter || {}, {
          securities: { $merge: subQuery },
        })
      }
      break
    }
    case "beta": {
      let subQuery = operator.queryFormatter(id, operator.value, dataType, ...values)
      subQuery = {
        [id]: subQuery,
      }
      if (!get(filter, "beta")) {
        filter = update(filter || {}, {
          beta: { $set: subQuery },
        })
      } else if (editingFilter) {
        filter = update(filter || {}, {
          beta: { $merge: subQuery },
        })
      } else {
        filter = update(filter || {}, {
          beta: { $merge: subQuery },
        })
      }
      break
    }
    case "classification": {
      const subQuery = {
        ...operator.queryFormatter(id, operator.value, dataType, ...values),
        id,
      }
      if (!get(filter, "classification")) {
        filter = update(filter || {}, {
          classification: { $set: [subQuery] },
        })
      } else if (editingFilter) {
        filter = update(filter || {}, {
          classification: { [editingFilter.id]: { $set: subQuery } },
        })
      } else {
        filter = update(filter || {}, {
          classification: { $push: [subQuery] },
        })
      }
      break
    }
    case "tag":
    case "positionAttributes": {
      filter = computeAddMembershipFilter(type, operator, id, dataType, values, filter, editingFilter)
      break
    }
  }

  return filter
}

export function computeRemoveFilter(originalFilter, filterType, id) {
  let filter = cloneDeep(originalFilter)
  switch (filterType) {
    case "longshort":
    case "descriptor":
      filter = omit(filter, id)
      break
    case "risk": {
      filter = update(filter, { risk: { $splice: [[id, 1]] } })
      if (filter.risk.length === 0) {
        delete filter.risk
      }
      break
    }
    case "factorExposure": {
      filter = update(filter, { factorExposure: { $splice: [[id, 1]] } })
      if (filter.factorExposure.length === 0) {
        delete filter.factorExposure
      }
      break
    }
    case "predictedFactorSensitivity":
    case "historicalFactorSensitivity": {
      filter = update(filter, { factorSensitivity: { $splice: [[id, 1]] } })
      if (filter.factorSensitivity.length === 0) {
        delete filter.factorSensitivity
      }
      break
    }
    case "universe": {
      filter = update(filter, { universe: { $splice: [[id, 1]] } })
      if (filter.universe.length === 0) {
        delete filter.universe
      }
      break
    }
    case "securities": {
      filter = omit(filter, `securities.${id}`)
      if (size(filter.securities.in) === 0 && size(filter.securities.notIn) === 0) {
        delete filter.securities
      }
      break
    }
    case "beta": {
      filter = omit(filter, `beta.${id}`)
      if (isEmpty(filter.beta)) {
        delete filter.beta
      }
      break
    }
    case "classification": {
      filter = update(filter, { classification: { $splice: [[id, 1]] } })
      if (filter.classification.length === 0) {
        delete filter.classification
      }
      break
    }
    case "tag": {
      filter = update(filter, { tag: { $splice: [[id, 1]] } })
      if (size(filter.tag) === 0) {
        delete filter.tag
      }
      break
    }
    case "positionAttributes": {
      filter = update(filter, { positionAttributes: { $splice: [[id, 1]] } })
      if (isEmpty(filter.positionAttributes)) {
        delete filter.positionAttributes
      }
      break
    }
  }

  if (keys(filter).length === 0) {
    filter = null
  }

  return filter
}

export function computeUpdatedFilters(filters) {
  let newFilter

  for (const filter of filters) {
    const { filterOn: { type, dataType, value: id, contentSetId }, operator, values } = filter
    // TODO: [APP-1404] once the computeAddFilter is removed, we can remove the editingFilter parameter
    newFilter = computeAddFilter(newFilter, type, dataType, id, operator, values, null, contentSetId)
  }
  return { filter: newFilter, addedFilters: (filters ?? []).map(f => ({ ...f, isExisting: true })) }
}

function recursiveGetClassificationFilter(filter, id, value, tier = 0) {
  if (value.values) {
    return value.values.flatMap(v => recursiveGetClassificationFilter(filter, id, v, tier + 1))
  }
  const hasCurrentClassificationFilter = filter && filter?.classifications?.some(c => c.id === id && c.eq.value === value.id)
  return {
    ...value,
    value: value.id,
    label: value.name,
    count: hasCurrentClassificationFilter ? 1 : 0,
  }
}

export function useFilterSecuritiesData(modelId, account, instrument, secondInstrument, comparisonInstrument, currentDate, isExpanded) {
  const [instrumentFilterSecurities, instrumentFilterSecuritiesStatus] = useFilterSecurities(modelId, account, instrument, currentDate, currentDate, isExpanded)
  const [secondInstrumentFilterSecurities, secondInstrumentFilterSecuritiesStatus] = useFilterSecurities(modelId, account, secondInstrument, currentDate, currentDate, isExpanded)
  const [comparisonInstrumentFilterSecurities, comparisonInstrumentFilterSecuritiesStatus] = useFilterSecurities(modelId, account, comparisonInstrument, currentDate, currentDate, isExpanded)

  const filterSecurities = useMemo(() => {
    const instruments = compact([
      instrument && { filterInstrument: instrument, securities: instrumentFilterSecurities },
      secondInstrument && { filterInstrument: secondInstrument, securities: secondInstrumentFilterSecurities },
      comparisonInstrument && { filterInstrument: comparisonInstrument, securities: comparisonInstrumentFilterSecurities },
    ])

    return instruments.reduce((acc, securitiesInstrument) => {
      if (!securitiesInstrument) {
        return acc
      }

      const { filterInstrument, securities } = securitiesInstrument

      // If the instrument is an inline position set, we need to build out the model provider ids and swap tickers
      if (filterInstrument?.dates) {
        ASSET_TYPES.forEach((assetType) => {
          const assetTypeDate = filterInstrument.dates[0][assetType]
          if (!assetTypeDate) {
            return
          }

          switch (assetType) {
            case "indexes":
              acc.modelProviderIds.push(...assetTypeDate.map(date => `i.${date.id}`))
              break
            case "swaps":
              acc.swapTickers.push(...assetTypeDate.map(date => date.id))
              break
            default:
              acc.modelProviderIds.push(...assetTypeDate.map(date => date.id.modelProviderId))
              break
          }
        })
      } else if (securities) {
        acc.modelProviderIds = union(acc.modelProviderIds, securities.modelProviderIds)
        acc.swapTickers = union(acc.swapTickers, securities.swapTickers)
      }

      return acc
    }, { modelProviderIds: [], swapTickers: [] })
  }, [instrumentFilterSecurities, secondInstrumentFilterSecurities, comparisonInstrumentFilterSecurities, instrument, secondInstrument, comparisonInstrument])

  return [
    filterSecurities,
    combineTanStackStatuses([
      instrumentFilterSecuritiesStatus,
      secondInstrumentFilterSecuritiesStatus,
      comparisonInstrumentFilterSecuritiesStatus,
    ]),
  ]
}

export function useClassificationsData(currentDate, filter) {
  const [classifications, classificationsStatus] = useClassifications()
  const [classificationsValues, classificationsValuesStatus] = useClassificationsValues(classifications, currentDate)

  const flattenedClassificationsValues = useMemo(() => {
    if (isEmpty(classificationsValues)) {
      return []
    }
    return Object.entries(classificationsValues).reduce((acc, [id, classification]) => {
      if (!classification) {
        return acc
      }
      const flattenedValues = classification.version.values.flatMap(value => recursiveGetClassificationFilter(filter, id, value))
      return set(acc, [id, "version", "values"], flattenedValues)
    }, {})
  }, [classificationsValues, filter])

  return {
    classifications,
    classificationsValues,
    flattenedClassificationsValues,
    status: combineTanStackStatuses([classificationsStatus, classificationsValuesStatus]),
  }
}

export function computeAddedFilters(filter, filterOns, additionalParams = {}) {
  if (isEmpty(filter)) {
    return EMPTY_ARRAY
  }

  const addedFilters = []
  const filterKeys = Object.keys(filter)

  filterKeys.forEach((field, keyIndex) => {
    const filterType = FILTER_TYPES.find(ft => ft.field === field)

    if (filterType && filter[field] && filter[field].length !== 0) {
      const { fetcher } = filterType
      const filterMetadata = []

      fetcher(filterMetadata, {
        filter: filter[field],
        filterType,
        id: field,
        ...additionalParams,
      })

      filterMetadata.forEach(({ type, id }) => {
        const editing = { filterType: type, id }
        const computedFilter = computeRawFilter(filter, editing, filterOns, additionalParams.securitiesDescriptors)

        if (computedFilter) {
          addedFilters.push({
            id,
            type,
            filterOn: computedFilter.filter?.filterOn,
            operator: computedFilter.filter?.operator,
            values: computedFilter.filter?.values || [],
            isUnsupported: computedFilter.isUnsupported || false,
            isExisting: true,
            order: keyIndex,
          })
        }
      })
    }
  })

  return addedFilters.sort((a, b) => a.order - b.order)
}
